import Box from "../Box";

import styled from "styled-components";
import { CText } from "@carrefour/design-system-components-react";
import BaseDashboardCard from "../BaseDashboardCard";
import { Detention } from "../../types/operational";
import useBreakPoint from "../../utils/useBreakPoint";
import { useTranslation } from "react-i18next";
import { formatPercentage } from "../../utils/numberUtils";
import SelectOption from "../SelectOption";
import { SingleValue } from "react-select";
import { Choice } from "../../types/commercial";
import DeltaBadge from "../DeltaBadge";

const Text = styled(CText)`
  font-family: system-ui;
`;
const TextCustom = styled(CText)<{ color: string }>`
  font-family: system-ui;
  color: ${(props) => props?.color};
`;
const Table = styled.table`
  width: 100%;
  align-items: center;
  border-collapse: collapse;
`;
const Tr = styled.tr``;
const TrCustom = styled.tr<{ isLast: boolean }>`
  border-collapse: collapse;
  border-bottom: ${(props) => (props?.isLast ? "" : " 1px solid #f0f3f6")};
`;
const Th = styled.th<{ $textAlign?: string }>`
  text-align: ${(props) => props?.$textAlign};
`;
const Td = styled.td<{ $textAlign?: string }>`
  text-align: ${(props) => props?.$textAlign};
  padding-block: 8px;
`;
type TopPGCCardProps = {
  value: number;
  range: string;
  ranking: { rang: number; max: number };
  selectedRange: string;
  options: Array<{ label: string; value: string }>;
  title: string;
  listValue: Array<Detention>;
  isUnavailable: boolean;
  messageError?: string;
  isLoading: boolean;
  importedDateTime?: string;
  handleRangeChange: (choice: SingleValue<Choice>) => void;
  openInformationModal: (title?: string) => void;
};

const BoxCustom = styled(Box)<{ gap: string }>`
  gap: ${(props) => props.gap};
  display: flex;
`;
const BoxSelect = styled(Box)`
  width: 100%;
`;
const getColorTag = (
  value: number
): "default" | "success" | "error" | "warning" | "info" | undefined => {
  if (value === -1) return "default";

  if (value > 97) {
    return "success";
  } else if (value <= 97 && value > 94) {
    return "warning";
  }

  return "error";
};
const getColorText = (value: number): string => {
  if (value === -1) return "black";

  if (value > 97) {
    return "#33691E";
  } else if (value <= 97 && value > 94) {
    return "#CC4010";
  }

  return "#AA0D11";
};
const TopPGCCard = ({
  listValue,
  value,
  selectedRange,
  ranking,
  handleRangeChange,
  range,
  options,
  title,
  importedDateTime,
  ...res
}: TopPGCCardProps) => {
  const isBreak = useBreakPoint();
  const {t} =useTranslation();

  return (
    <BaseDashboardCard
      hasModal={true}
      title={title}
      size={"big"}
      importedDateTime={importedDateTime}
      option={
        <BoxSelect py={1}>
          <SelectOption
            list={options}
            value={selectedRange}
            width={"100%"}
            handleChange={handleRangeChange}
          />
        </BoxSelect>
      }
      {...res}
    >
      <BoxCustom
        gap="1.5rem"
        justifyContent={"center"}
        alignItems={"center"}
        flexDirection={"column"}
      >
        <BoxCustom
          gap={`0.8rem`}
          flexDirection={"column"}
          alignItems={"center"}
          width="100%"
          borderRadius={1}
          pt={[1, 4]}
        >
          <Box display={"flex"} justifyContent={"center"} width={"100%"}>
            {/* Détention causes magasin */}
            <Box
              borderRadius={1}
              border={"1px solid #BDC7DA"}
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"center"}
              alignItems={"center"}
              py={1}
              px={2}
              width={["auto", "50%"]}
            >
              <Box display={"flex"} alignItems={"center"} pt={1}>
                <Text size={"s"} tagStyle="p">
                 {t('operationnel.detention_cause_magasin')}
                </Text>
              </Box>
              <Box
                display={"flex"}
                alignItems={"baseline"}
                py={"4px"}
                pt={[0, 0]}
              >
                <TextCustom
                  color={getColorText(value)}
                  size={"m"}
                  tagStyle="h1"
                >{`${formatPercentage(value, "")}`}</TextCustom>
                <TextCustom
                  color={getColorText(value)}
                  tagStyle="p"
                >{`%`}</TextCustom>
              </Box>
            </Box>
          </Box>
        </BoxCustom>
        {/* famille */}
        <Box alignItems="center" justifyContent={"center"} width="100%">
          <Table>
            <thead>
              <Tr>
                <Th $textAlign={"left"}>
                  <Text bold size={"s"} tagStyle={"subtitle"}>
                    {t('operationnel.famille')}
                  </Text>
                </Th>
                <Th></Th>
              </Tr>
            </thead>
            <tbody>
              {listValue &&
                listValue?.length > 0 &&
                listValue?.map((detention, index) =>
                  subElements(
                    detention,
                    isBreak,
                    listValue?.length === index + 1
                  )
                )}
            </tbody>
          </Table>
        </Box>
      </BoxCustom>
    </BaseDashboardCard>
  );
};

export default TopPGCCard;

function subElements(detention: Detention, isBreak: string, isLast: boolean) {
  return (
    <TrCustom isLast={isLast} key={"topPgc" + detention.departmentKey}>
      <Td $textAlign={"left"}>
        <BoxCustom gap="2px" display={"flex"}>
          <Text size={"m"} tagStyle="p">
            {detention.departmentKey}
          </Text>
          <Text size={"m"} tagStyle="p">
            {":"}
          </Text>
          <Text size={"m"} tagStyle="p">
            {detention.departmentDesc}
          </Text>
        </BoxCustom>
      </Td>
      <Td $textAlign={"right"}>
        <DeltaBadge
          value={detention.rate}
          status={getColorTag(detention.rate)}
          showIcon={false}
          showSign={false}
        />
      </Td>
    </TrCustom>
  );
}
